import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  EnvelopeSimple, User, UsersThree, Check, GoogleLogo, MicrosoftOutlookLogo,
  PaperPlaneTilt, LinkSimple, ArrowRight, SignOut
} from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { AgentLanguageSelect } from "@/components/AgentLanguageCard";
import { PromoCodeInput } from "@/components/PromoCodeInput";
import { useAuth } from "@/App";
import { t } from "@/lib/i18n";

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { logout, refreshUser } = useAuth();
  const [me, setMe] = useState(null);
  const [packages, setPackages] = useState([]);
  const [usageType, setUsageType] = useState("");
  const [buying, setBuying] = useState("");
  const [promoCode, setPromoCode] = useState(null);
  const [promoInfo, setPromoInfo] = useState(null);
  const [brandName, setBrandName] = useState("Agentic Inbox");

  useEffect(() => {
    api.get("/site/landing").then((r) => setBrandName(r.data?.brand?.app_name || "Agentic Inbox")).catch(() => {});
  }, []);
  const [inviteEmail, setInviteEmail] = useState("");
  const [invites, setInvites] = useState([]);
  const [team, setTeam] = useState(null);
  const [obLang, setObLang] = useState("auto");
  const [prefPlanId, setPrefPlanId] = useState(() => localStorage.getItem("agi_pref_plan") || "");

  const load = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setMe(data);
    setUsageType(data.usage_type || "");
    if (data.team_id) api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); api.get("/packages").then((r) => setPackages(r.data)); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "gmail") {
      toast.success("Gmail connected! Importing your mailbox — this takes a few seconds…");
      window.history.replaceState({}, "", "/onboarding");
    } else if (params.get("connect_error")) {
      toast.error(`Gmail connection failed: ${params.get("connect_error")} — please try again`);
      window.history.replaceState({}, "", "/onboarding");
    }
  }, []);

  if (!me) return <div className="h-screen flex items-center justify-center text-sm text-gray-500">{t("common.loading", "Loading…")}</div>;
  const stage = me.onboarding_stage || "done";

  const pickUsage = async (t) => {
    try { await api.post("/auth/onboarding/usage-type", { usage_type: t }); setUsageType(t); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const choosePlan = async (pkg) => {
    setBuying(pkg.id);
    try {
      const { data } = await api.post("/billing/checkout", { package_id: pkg.id, origin_url: window.location.origin, code: promoCode || undefined });
      localStorage.removeItem("agi_pref_plan");
      if (data.free) { toast.success(data.message); await refreshUser(); await load(); }
      else window.location.href = data.url;
    } catch (e) { toast.error(errDetail(e)); }
    setBuying("");
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/team/invite", { email: inviteEmail, origin_url: window.location.origin });
      setInvites((l) => [...l, { email: inviteEmail, link: data.invite_link, emailed: data.sent_via_email }]);
      toast.success(data.sent_via_email ? "Invitation emailed!" : "Invitation created — share the link");
      setInviteEmail("");
    } catch (err) { toast.error(errDetail(err)); }
  };

  const connect = async (provider) => {
    if (provider === "gmail") {
      try {
        const { data } = await api.get("/oauth/gmail/start?from_page=onboarding");
        window.location.href = data.url;
      } catch (e) { toast.error(errDetail(e)); }
      return;
    }
    try {
      const { data } = await api.post("/connections/connect", { provider });
      data.configured ? toast.success(data.message) : toast.info(data.message, { duration: 8000 });
    } catch (e) { toast.error(errDetail(e)); }
  };

  const finish = async () => {
    try {
      await api.post("/auth/onboarding/complete");
      await refreshUser();
      toast.success("You're all set!");
      navigate("/app");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const audienceOf = (p) => p.audience || (p.seats <= 1 ? "personal" : "team");
  const visiblePackages = packages.filter((p) => audienceOf(p) === (usageType === "personal" ? "personal" : "team"));
  const prefPlan = prefPlanId ? packages.find((p) => p.id === prefPlanId) : null;

  // Gap 11: invited team members never pick plans — ignore any remembered plan
  if (me.team_id && prefPlanId) { localStorage.removeItem("agi_pref_plan"); setPrefPlanId(""); }
  // Gap 7: remembered plan pre-answers the usage-type question (user can still switch)
  if (prefPlan && !usageType && stage === "choose_plan" && !me.team_id) { pickUsage(audienceOf(prefPlan)); }
  // Gap 11: stale/hidden plan → silently fall back to the normal list
  if (prefPlanId && packages.length > 0 && !prefPlan) { localStorage.removeItem("agi_pref_plan"); setPrefPlanId(""); }

  const isTeamAdmin = me.team_role === "team_admin" && usageType !== "personal";

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-6" data-testid="ob-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center"><EnvelopeSimple size={20} weight="bold" className="text-white" /></div>
            <span className="font-heading font-bold text-xl text-gray-900">{brandName}</span>
          </div>
          <button data-testid="ob-logout" onClick={logout} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"><SignOut size={15} /> Sign out</button>
        </div>

        {stage === "choose_plan" && !usageType && (
          <div className="fade-up">
            <h1 className="font-heading text-3xl font-bold text-gray-900 mb-1">{t("onboard.how_use", "How will you use {brand}?", { brand: brandName })}</h1>
            <p className="text-sm text-gray-500 mb-8">{t("onboard.decides_plans", "This decides which plans we show you. You can't skip this step.")}</p>
            <div className="grid grid-cols-2 gap-5">
              <button data-testid="ob-usage-personal" onClick={() => pickUsage("personal")}
                className="bg-white border-2 border-gray-200 hover:border-blue-600 rounded-md p-8 text-left transition-colors group">
                <User size={28} className="text-blue-600 mb-3" />
                <div className="font-heading font-bold text-lg text-gray-900">{t("onboard.personal", "Personal")}</div>
                <p className="text-sm text-gray-500 mt-1">{t("onboard.personal_sub", "Just me — connect my own mailbox and let the agent manage it.")}</p>
              </button>
              <button data-testid="ob-usage-team" onClick={() => pickUsage("team")}
                className="bg-white border-2 border-gray-200 hover:border-blue-600 rounded-md p-8 text-left transition-colors group">
                <UsersThree size={28} className="text-blue-600 mb-3" />
                <div className="font-heading font-bold text-lg text-gray-900">{t("onboard.team", "Team")}</div>
                <p className="text-sm text-gray-500 mt-1">I'm buying seats for my team — like Google Workspace. I'll invite members.</p>
              </button>
            </div>
          </div>
        )}

        {stage === "choose_plan" && usageType && (
          <div className="fade-up">
            <h1 className="font-heading text-3xl font-bold text-gray-900 mb-1">Pick your {usageType} plan</h1>
            <p className="text-sm text-gray-500 mb-2">A plan is required before you can use the app. <button data-testid="ob-change-usage" onClick={() => setUsageType("")} className="text-blue-600 underline">{t("onboard.change_type", "Change use type")}</button></p>
            <p className="text-sm mb-8">
              <button data-testid="ob-switch-audience" onClick={() => pickUsage(usageType === "personal" ? "team" : "personal")}
                className="text-blue-600 underline">
                {usageType === "personal" ? "Buying for a team instead? → See team plans" : "Just for yourself? → See personal plans"}
              </button>
            </p>
            <div className="mb-4">
              <PromoCodeInput kind="package" onApplied={(c, d) => { setPromoCode(c); setPromoInfo(d); }} testId="ob-promo" />
              {promoInfo?.allowed_package_ids?.length > 0 && (
                <p data-testid="ob-promo-scope-note" className="text-[11px] text-amber-700 mt-1.5">
                  {t("promo.plan_scope", "This code only applies to:")} {packages.filter((p) => promoInfo.allowed_package_ids.includes(p.id)).map((p) => p.name).join(", ")}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {visiblePackages.map((pkg) => {
                const slug = pkg.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                const isPref = prefPlanId === pkg.id;
                return (
                  <div key={pkg.id} data-testid={`ob-plan-card-${slug}`} className={`bg-white rounded-md p-6 flex flex-col relative ${isPref ? "border-2 border-emerald-500 shadow-lg" : pkg.popular ? "border-2 border-blue-600" : "border border-gray-200"}`}>
                    {isPref && <span data-testid="ob-pref-badge" className="absolute -top-3 left-4 bg-emerald-500 text-white text-[11px] font-bold rounded-full px-2.5 py-0.5">{t("onboard.your_selection", "Your selection")}</span>}
                    <div className="font-heading font-bold text-gray-900">{pkg.name}</div>
                    <div className="mt-1 mb-1"><span className="font-heading text-3xl font-extrabold">${pkg.price_monthly % 1 === 0 ? pkg.price_monthly.toFixed(0) : pkg.price_monthly}</span><span className="text-xs text-gray-500">/mo</span></div>
                    <p className="text-xs text-gray-500 mb-4">{pkg.seats} seat{pkg.seats > 1 ? "s" : ""}</p>
                    <ul className="space-y-1.5 flex-1 mb-5">
                      {(pkg.features || []).slice(0, 4).map((f) => <li key={f} className="flex items-start gap-1.5 text-xs text-gray-600"><Check size={13} className="text-blue-600 mt-0.5 shrink-0" />{f}</li>)}
                    </ul>
                    <button data-testid={`ob-plan-choose-${slug}`} onClick={() => choosePlan(pkg)}
                      disabled={buying === pkg.id || (promoInfo?.allowed_package_ids?.length > 0 && !promoInfo.allowed_package_ids.includes(pkg.id))}
                      title={promoInfo?.allowed_package_ids?.length > 0 && !promoInfo.allowed_package_ids.includes(pkg.id) ? t("promo.not_for_plan", "Your code doesn't apply to this plan") : undefined}
                      className={`w-full py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${pkg.popular ? "bg-blue-600 hover:bg-blue-700 text-white" : "border border-gray-300 hover:bg-gray-100 text-gray-800"}`}>
                      {buying === pkg.id ? "Redirecting…" : promoInfo?.allowed_package_ids?.length > 0 && !promoInfo.allowed_package_ids.includes(pkg.id) ? t("promo.code_na", "Code not valid here") : pkg.price_monthly <= 0 ? "Start free" : `Get ${pkg.name}`}
                    </button>
                  </div>
                );
              })}
              {visiblePackages.length === 0 && (
                <p data-testid="ob-empty-tab" className="text-sm text-gray-500 col-span-3">
                  No {usageType} plans published yet —{" "}
                  <button onClick={() => pickUsage(usageType === "personal" ? "team" : "personal")} className="text-blue-600 underline">
                    see the {usageType === "personal" ? "team" : "personal"} plans →</button>
                </p>
              )}
            </div>
          </div>
        )}

        {stage === "setup" && (
          <div className="fade-up" data-testid="ob-setup-step">
            <h1 className="font-heading text-3xl font-bold text-gray-900 mb-1">{t("onboard.setup_workspace", "Set up your workspace")}</h1>
            <p className="text-sm text-gray-500 mb-8">{isTeamAdmin ? "Invite your team, connect your mailbox, and you're in." : "Connect your mailbox and you're in."}</p>

            {isTeamAdmin && team && (
              <div className="bg-white border border-gray-200 rounded-md p-5 mb-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading font-bold text-gray-900 flex items-center gap-2"><UsersThree size={17} className="text-blue-600" /> Invite your team</h2>
                  <span data-testid="ob-seat-count" className="text-xs text-gray-500">{team.seats_used} of {team.seats_total} seats used</span>
                </div>
                <form onSubmit={sendInvite} className="flex gap-2">
                  <input data-testid="ob-invite-email-input" type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@company.com" className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <button data-testid="ob-invite-btn" className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 transition-colors"><PaperPlaneTilt size={14} /> Invite</button>
                </form>
                {invites.map((inv, i) => (
                  <div key={i} data-testid={`ob-invite-link-${i}`} className="mt-2 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-xs text-blue-900">
                    <LinkSimple size={13} className="shrink-0" /><span className="font-medium">{inv.email}</span>
                    <span className="text-blue-600">{inv.emailed ? "— invitation emailed ✓" : ""}</span>
                    <code className="break-all flex-1 text-blue-700">{inv.link}</code>
                  </div>
                ))}
                <p className="text-xs text-gray-400 mt-2">{t("onboard.manage_later", "You can also manage members later from the Team page.")}</p>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-md p-5 mb-5" data-testid="ob-agent-language">
              <h2 className="font-heading font-bold text-gray-900 mb-1">{t("onboard.agent_language", "Your agent's language")}</h2>
              <p className="text-xs text-gray-500 mb-3">In which language should your agent talk to you? (Emails to contacts keep their own language.)</p>
              <AgentLanguageSelect value={obLang} onChange={(v) => { setObLang(v); api.put("/me/agent-language", { language: v }).catch(() => {}); }} testId="ob-agent-language-select" />
            </div>

            <div className="bg-white border border-gray-200 rounded-md p-5 mb-6">
              <h2 className="font-heading font-bold text-gray-900 mb-3 flex items-center gap-2"><EnvelopeSimple size={17} className="text-blue-600" /> Connect your email</h2>
              <div className="grid grid-cols-2 gap-3">
                <button data-testid="ob-connect-gmail" onClick={() => connect("gmail")}
                  className="flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-sm font-medium rounded-md py-2.5 transition-colors">
                  <GoogleLogo size={17} weight="bold" className="text-red-500" /> Connect Gmail
                </button>
                <button data-testid="ob-connect-outlook" onClick={() => connect("outlook")}
                  className="flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-sm font-medium rounded-md py-2.5 transition-colors">
                  <MicrosoftOutlookLogo size={17} weight="bold" className="text-blue-500" /> Connect Outlook
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">{t("onboard.later_settings", "You can also do this later from Settings.")}</p>
            </div>

            <button data-testid="ob-finish-btn" onClick={finish}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md px-6 py-3 text-sm transition-colors">
              Go to my inbox <ArrowRight size={15} weight="bold" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
